import { onUnmounted } from "vue";

const DEFAULT_INTERVAL_MS = 5000;

/**
 * Composable that polls a method at a fixed interval and stops when a condition is met.
 * Automatically clears the interval on component unmount.
 *
 * @param method - Async function called on each tick
 * @param shouldStop - Called after each tick; polling stops when it returns true
 * @param intervalMs - Poll interval in milliseconds (default: 5000)
 */
export function usePolling(
  method: () => Promise<void>,
  shouldStop: () => boolean,
  intervalMs: number = DEFAULT_INTERVAL_MS
) {
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const stopPolling = () => {
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const startPolling = () => {
    if (pollInterval !== null) return;
    pollInterval = setInterval(async () => {
      await method();
      if (shouldStop()) {
        stopPolling();
      }
    }, intervalMs);
  };

  onUnmounted(() => {
    stopPolling();
  });

  return { startPolling, stopPolling };
}
